import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";

const UNIVERSITIES = [
  { id: "utcb", name: "UTCB", full: "Universitatea Tehnică de Construcții București" },
  { id: "utm", name: "UTM", full: "Universitatea Titu Maiorescu din București" },
  { id: "usvt", name: "USVT", full: "Universitatea de Științele Vieții „Regele Mihai I” din Timișoara" },
  { id: "upg", name: "UPG", full: "Universitatea de Petrol și Gaze din Ploiești" },
  { id: "umfcd", name: "UMFCD", full: "Universitatea de Medicină și Farmacie „Carol Davila” din București" },
  { id: "umc", name: "UMC", full: "Universitatea Maritimă din Constanța" },
  { id: "uauim", name: "UAUIM", full: "Universitatea de Arhitectură și Urbanism „Ion Mincu” din București" },
  { id: "ovidius", name: "Ovidius", full: "Universitatea Ovidius din Constanța" },
  { id: "uvvg", name: "UVVG", full: "Universitatea de Vest „Vasile Goldiș” din Arad" },
];

const normalize = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function Home() {
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "ISIC — Alege universitatea ta";
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return UNIVERSITIES;
    return UNIVERSITIES.filter(
      (u) => normalize(u.name).includes(q) || normalize(u.full).includes(q)
    );
  }, [query]);

  return (
    <main className="min-h-screen bg-white" data-testid="home-page">
      <div className="mx-auto flex max-w-4xl flex-col items-center px-4 py-12 sm:px-6 md:py-20">
        {/* Header */}
        <header className="mb-8 w-full text-center">
          <h1
            className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
            data-testid="home-title"
          >
            Alege universitatea ta
          </h1>
          <p className="mt-2 text-base text-gray-600" data-testid="home-subtitle">
            Selectează instituția la care studiezi pentru a continua.
          </p>
        </header>

        {/* Search */}
        <div className="relative mb-8 w-full max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            strokeWidth={2}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Caută universitatea"
            aria-label="Caută universitatea"
            className="h-12 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-base text-gray-900 placeholder:text-gray-400 transition-colors focus:border-[#2f68b0] focus:outline-none focus:ring-2 focus:ring-[#2f68b0]/40"
            data-testid="search-universities-input"
          />
        </div>

        {/* Universities grid */}
        {filtered.length > 0 ? (
          <div
            className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="home-universities"
          >
            {filtered.map((u) => (
              <Link
                key={u.id}
                to={`/${u.id}`}
                title={u.full}
                className="flex h-12 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-center text-sm font-semibold text-gray-800 transition-colors hover:border-[#2f68b0] hover:bg-[#f3f8fe] hover:text-[#2f68b0] focus:outline-none focus:ring-2 focus:ring-[#2f68b0]/50 sm:h-14"
                data-testid={`university-link-${u.id}`}
              >
                {u.name}
              </Link>
            ))}
          </div>
        ) : (
          <p className="w-full py-8 text-center text-sm text-gray-500" data-testid="home-empty-state">
            Nu s-au găsit rezultate.
          </p>
        )}

        {/* Fallback link */}
        <footer className="mt-12 w-full text-center">
          <p className="text-sm text-gray-500" data-testid="home-footer-note">
            Nu găsești universitatea ta?{" "}
            <a
              href="https://www.isic.ro"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm font-medium text-[#2f68b0] hover:underline focus:outline-none focus:ring-2 focus:ring-[#2f68b0]/50"
              data-testid="footer-isic-link"
            >
              Obține legitimația ISIC aici.
            </a>
          </p>
        </footer>
      </div>
    </main>
  );
}
